import { useCallback, useEffect, useState } from "preact/hooks";

import { ConsentBanner, loadConsent } from "../consent/Banner";
import type { ConsentRecord } from "../consent/state";
import { type EngagementType, installEngagementFlushHooks } from "../feed/engagement";
import { Feed } from "../feed/Feed";
import {
  applyUpdate,
  type InterestVector,
  loadInterestVector,
  saveInterestVector,
} from "../feed/interest";
import { trackEngagement } from "../feed/track";
import type { ManifestEntry } from "../feed/types";
import { bootstrapIdentity } from "../identity/bootstrap";
import { Onboarding } from "../onboarding/Onboarding";
import { isOnboarded } from "../onboarding/state";
import { Settings } from "../settings/Settings";
import { useRankPrefs } from "../settings/rankPrefs";
import { Header } from "./Header";

export function App() {
  const [consent, setConsent] = useState<ConsentRecord | null>(loadConsent());
  const [online, setOnline] = useState(navigator.onLine);
  const [interest, setInterest] = useState<InterestVector | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // The currently-loaded manifest entries, surfaced up from Feed so the
  // Settings drawer can render a live re-rank preview (the drawer covers
  // the feed, so this is how the reader watches their tuning take effect).
  const [feedEntries, setFeedEntries] = useState<ManifestEntry[]>([]);
  // Persisted ranking knobs. Available pre-consent too (the sliders work
  // even with no interest vector — rank.ts treats null as a no-op).
  const [rankParams, setRankParams] = useRankPrefs();

  // Online/offline indicator — no identity work here.
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // THE IDENTITY GATE. Nothing here runs — no id is generated, stored, or
  // transmitted — unless consent has been *accepted*. Declining (or no
  // decision) leaves IndexedDB and cookies free of any identifier; the feed
  // still works in plain base-score order.
  const personalized = consent?.decision === "accepted";

  useEffect(() => {
    if (!personalized) {
      setInterest(null);
      setNeedsOnboarding(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // Bind identity (anonymous id + fingerprint → device cookie). Best
      // effort: a failed bind doesn't block the personalized UI.
      await bootstrapIdentity(consent);
      if (cancelled) return;
      installEngagementFlushHooks();
      const onboarded = await isOnboarded();
      if (cancelled) return;
      if (!onboarded) {
        setNeedsOnboarding(true);
        return;
      }
      const vec = await loadInterestVector();
      if (!cancelled) setInterest(vec);
    })();
    return () => {
      cancelled = true;
    };
  }, [personalized]);

  const onOnboardingComplete = async () => {
    setNeedsOnboarding(false);
    setInterest(await loadInterestVector());
  };

  // Stable engagement sink. Wrapped in useCallback (rather than an
  // inline arrow on the Feed prop) so children that put `onEngage` in
  // their own useMemo / useCallback dep arrays get a reference-stable
  // value across parent re-renders — Feed.tsx's `onSelectRelated`
  // memo only delivers on its "stable across renders" promise if
  // every dep up the chain is stable too. Identity changes only when
  // `personalized` flips, which is when the engagement contract
  // actually changes shape (recording vs not).
  const onEngage = useCallback(
    (type: EngagementType, entry: ManifestEntry) => {
      if (!personalized) return;
      void trackEngagement(type, entry);
    },
    [personalized],
  );

  // Boost the on-device interest vector toward the given tags and update
  // App state so the feed re-ranks live. Used by the end-of-feed "what
  // do you want more of?" prompt. applyUpdate is the same decay-then-add
  // nudge the engagement path uses; +1.0 is a strong, deliberate signal.
  const boostInterest = useCallback(
    (tags: readonly string[]) => {
      void (async () => {
        const next = applyUpdate(interest ?? {}, [...tags], 1.0);
        const saved = await saveInterestVector(next);
        setInterest(saved);
      })();
    },
    [interest],
  );

  const onForgotten = () => {
    // Back to a clean, pre-consent slate.
    setShowSettings(false);
    setInterest(null);
    setNeedsOnboarding(false);
    setConsent(null);
  };

  return (
    <div class="app">
      <Header
        online={online}
        onOpenSettings={() => setShowSettings(true)}
      />
      <main>
        {needsOnboarding ? (
          <Onboarding onComplete={() => void onOnboardingComplete()} />
        ) : (
          <Feed
            interest={interest}
            rankParams={rankParams}
            onEngage={personalized ? onEngage : undefined}
            onBoostTopics={personalized ? boostInterest : undefined}
            onEntriesLoaded={setFeedEntries}
          />
        )}
      </main>
      {!consent ? <ConsentBanner onResolved={setConsent} /> : null}
      {showSettings ? (
        <Settings
          onClose={() => setShowSettings(false)}
          onForgotten={onForgotten}
          rankParams={rankParams}
          setRankParams={setRankParams}
          personalized={personalized}
          onConsentChange={setConsent}
          previewEntries={feedEntries}
          liveInterest={interest}
        />
      ) : null}
    </div>
  );
}
